import { SystemModule } from "@comact/crc";
import _ from "lodash";
import { memoize } from "proxy-memoize";
import { getPermissionKey, IPermissionKey, IPermissionLevel } from "./model";

export const getMyAccount = ({ myAccount }: IStoreState) => myAccount;
export const isLoggedIn = (state: IStoreState): boolean => !!getMyAccount(state);

export const getUserSiteInheritedPermissions = (state: IStoreState, userId: string, siteNodeId: string): IPermissionKey[] => {
    const user = getUserById(state, userId);
    if (!user) return [];

    return _.uniq(getSitePermissionsForGroupIds(state, user.groupIds, siteNodeId));
};

export const getSitePermissionsForGroupIds = (state: IStoreState, groupIds: string[], siteNodeId: string) => (
    _.reduce(groupIds, (accumulator, groupId) => {
        const group = getGroupById(state, groupId);
        if (!group) return accumulator;
        // is iep group or is icp group that is on the desired mill
        const siteNodeIds = group.siteNodeIds;
        if (!group.siteNodeIds || group.allSiteAccess || _.some(siteNodeIds, (id) => siteNodeId.endsWith(id))) {
            _.forEach(group.permissions, (permission) => {
                if (!_.includes(accumulator, permission)) accumulator.push(permission);
            });
        }
        return accumulator;
    }, [] as IPermissionKey[])
);

const getUserSitePermissions = (state: IStoreState, userId: string, siteNodeId: string): IPermissionKey[] => {
    const user = getUserById(state, userId);
    if (!user) return [];

    const userSitePermissionsInheritedFromGroups = getUserSiteInheritedPermissions(state, userId, siteNodeId);
    const userSitePermissions = user.permissions[siteNodeId] || { allowed: [], denied: [] };

    return _.chain(userSitePermissionsInheritedFromGroups) // take permissions inherited from group
        .union(userSitePermissions.allowed) // add user allowed permissions
        .difference(userSitePermissions.denied) // remove user denied permissions
        .value();
};

/**
 * System permissions don't really exist for now but we combine all the mills permissions to have them.
 */
const getUserSystemPermissions = memoize(({ state, userId }: { state: IStoreState; userId: string; }) => {
    const user = getUserById(state, userId);
    if (!user) return [];

    // Groups mill mixed permissions
    const userSystemPermissionsInheritedFromGroups = _.reduce(user.groupIds, (accumulator, groupId) => {
        const group = getGroupById(state, groupId);
        if (!group) return accumulator;
        accumulator.push(...group.permissions);
        return accumulator;
    }, [] as IPermissionKey[]);

    // User site mixed permissions
    const userAllSitesPermissions = _.reduce(user.permissions, (prev, { allowed, denied }) => {
        prev.allowed = _.union(prev.allowed, allowed);
        prev.denied = _.union(prev.denied, denied);
        return prev;
    }, { allowed: [], denied: [] });

    return _.chain(userSystemPermissionsInheritedFromGroups) // take permissions inherited from group
        .union(userAllSitesPermissions.allowed) // add user allowed permissions
        .difference(userAllSitesPermissions.denied) // remove user denied permissions
        .value();
});

/**
 * ICP only: Check if the current user has view permission on the mill node. We use the list of checked off mills as the source of truth for this.
 */
export const currentUserHasViewPermission = memoize(({ state, siteNodeId }: { state: IStoreState; siteNodeId: string; }) => {
    const user = getCurrentUser(state);
    if (!user) return false;

    const groupSitePermissions = _.some(user.groupIds, (groupId) => {
        const group = getGroupById(state, groupId);
        const siteNodeIds = group.siteNodeIds;
        return group && (group.allSiteAccess || _.includes(siteNodeIds, siteNodeId));
    });

    const userSitePermissions = !_.isEmpty(user.permissions?.[siteNodeId]?.allowed);

    return groupSitePermissions || userSitePermissions;
});

export const hasPermission = (state: IStoreState, neededPermissions: IPermissionLevel[], siteNodeId?: string, byPass?: { cmoc?: boolean; icp?: boolean; }, needsAllPermissions: boolean = false) => {
    const neededPermissionsKeys: IPermissionKey[] = neededPermissions.map(({ permission, level }) => getPermissionKey({ permission, level }));
    if (byPass) {
        if (process.env.EXEC_MODE == "icp" && typeof byPass.icp == "boolean") return byPass.icp;
        if (process.env.EXEC_MODE == "cmoc" && typeof byPass.cmoc == "boolean") return byPass.cmoc;
    }
    const user = getCurrentUser(state);
    if (!user) return false;
    if (!state.organizationGroups) return false; // in case groups are not loaded yet

    // Explicit view permissions checked based on which mills the group's permissions are applied to.
    if (process.env.EXEC_MODE == "icp" && _.isEmpty(neededPermissionsKeys)) return !siteNodeId || currentUserHasViewPermission({ state, siteNodeId });

    const userTotalPermissions = siteNodeId // if we are node inside a mill, we will used the system permissions (location and organization/customer nodes)
        ? getUserSitePermissions(state, user.id, process.env.EXEC_MODE == "icp" ? siteNodeId : "*") // site permissions
        : getUserSystemPermissions({ state, userId: user.id }); // system permissions
    return needsAllPermissions
        ? _.every(neededPermissionsKeys, (p) => _.includes(userTotalPermissions, p))
        : _.some(neededPermissionsKeys, (p) => _.includes(userTotalPermissions, p));
};

export const getMyAccountOrganizations = ({ myAccountOrganizations }: IStoreState) => myAccountOrganizations;

export const getCurrentOrganization = ({ myAccountOrganizations, userOrganizationId }: IStoreState) => myAccountOrganizations?.[userOrganizationId];
export const getCurrentUser = (state: IStoreState) => _.find(getMyAccountUsers(state), ({ organizationId }) => organizationId == getUserOrganizationId(state));
export const getCurrentUserId = (state: IStoreState) => getCurrentUser(state)?.id;

// This is the unit systems of the current user. If the user is not logged in, it will return the default unit systems of the machine.
export const getUnitSystems = (state: IStoreState) => isLoggedIn(state) ? getCurrentUser(state)?.userUnitSystems : SystemModule.selectors.getAllSystem(state);

export const getUserOrganizationId = ({ userOrganizationId }: IStoreState) => userOrganizationId;

export const getMyAccountUsers = ({ myAccountUsers }: IStoreState) => myAccountUsers;

export const getOrganizationGroups = memoize(({ organizationGroups }: IStoreState) => organizationGroups ? _.pickBy(organizationGroups, ({ hidden }) => !hidden) : null);
export const getGroupById = ({ organizationGroups }: IStoreState, id: string) => id ? organizationGroups?.[id] : null;
export const getGroupExtraById = (state: IStoreState, groupId: string) => ({
    ...state.organizationGroups?.[groupId],
    users: getUsersByGroupId({ state, groupId }),
});

export const getGroupExtraWithPartialUserById = (state: IStoreState, groupId: string) => ({
    ...state.organizationGroups?.[groupId],
    users: getPartialUsersByGroupId({ state, groupId }),
});

export const getGroupExtraByGroupId = (state: IStoreState, externalGroupId: string) => {
    const group = _.find(state.organizationGroups, (g) => g?.externalGroupId === externalGroupId);

    return {
        ...(group ?? {}),
        users: group?.id
            ? getUsersByGroupId({ state, groupId: group.id })
            : null,
    };
};

/** This selector can be used to get the current user or any user (if the current user has permission "user-management") */
export const getUserById = (state: IStoreState, id: string) => {
    if (id == getCurrentUserId(state)) return getCurrentUser(state);
    return state.organizationUsers?.[id];
};

/** Only users with permission "user-management" can access organizationUsers */
export const getOrganizationUsers = memoize(({ organizationUsers }: IStoreState) => organizationUsers ? _.pickBy(organizationUsers, ({ hidden }) => !hidden) : null);

/** Any user can access partial users */
export const getOrganizationPartialUsers = memoize(({ organizationPartialUsers }: IStoreState) => organizationPartialUsers ? _.pickBy(organizationPartialUsers, ({ hidden }) => !hidden) : null);
export const getOrganizationActivePartialUsers = memoize(({ organizationPartialUsers }: IStoreState) => (
    organizationPartialUsers ? _.pickBy(organizationPartialUsers, ({ deleted, hidden }) => !deleted && !hidden) : null
));

export const getPartialUsersByGroupId = memoize(
    ({ state: { organizationPartialUsers }, groupId }: { state: IStoreState; groupId: string; }) => organizationPartialUsers
        ? _.pickBy(
            organizationPartialUsers, ({ groupIds }) => _.includes(groupIds, groupId)
        )
        : null
);

export const getPartialUserById = (state: IStoreState, id: string) => state.organizationPartialUsers?.[id];

export const getUsersByGroupId = memoize(
    ({ state: { organizationUsers }, groupId }: { state: IStoreState; groupId: string; }) => organizationUsers ? _.pickBy(organizationUsers, ({ groupIds }) => _.includes(groupIds, groupId)) : null
);